import { useState } from "react";
import { useParams, useNavigate, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { useEffect } from "react";
import { Lightning, ShieldCheck, Eye, EyeSlash, ArrowLeft } from "@phosphor-icons/react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function InviteAccept() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user, setSession } = useAuth();
  const [state, setState] = useState("loading"); // loading | valid | invalid
  const [info, setInfo] = useState(null);
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get(`/auth/invite/${token}`)
      .then(({ data }) => { setInfo(data); setState("valid"); })
      .catch((err) => { setError(formatApiError(err.response?.data?.detail)); setState("invalid"); });
  }, [token]);

  if (user) return <Navigate to="/" replace />;

  const accept = async (e) => {
    e.preventDefault(); setError(""); setLoading(true);
    try {
      const { data } = await api.post("/auth/accept-invite", { token, name, password });
      localStorage.setItem("pf_token", data.token);
      setSession({ id: data.id, email: data.email, name: data.name, role: data.role });
      toast.success("Welcome to PayFlow HR!");
      navigate("/");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#F4F5F7] p-6">
      <div className="w-full max-w-sm">
        <div className="mb-8 flex items-center gap-2.5">
          <div className="flex h-9 w-9 items-center justify-center rounded-md bg-red-500">
            <Lightning weight="fill" size={20} className="text-white" />
          </div>
          <span className="font-heading text-lg font-extrabold tracking-tight text-[#0F172A]">PayFlow HR</span>
        </div>

        {state === "loading" && (
          <div className="flex justify-center py-10">
            <div className="h-7 w-7 animate-spin rounded-full border-2 border-red-500 border-t-transparent" />
          </div>
        )}

        {state === "invalid" && (
          <div>
            <h1 className="font-heading text-2xl font-bold text-[#0F172A]">Invitation unavailable</h1>
            <p className="mt-2 text-sm text-red-400">{error}</p>
            <Button onClick={() => navigate("/login")} className="mt-6 bg-red-500 font-semibold text-white hover:bg-red-600">
              <ArrowLeft size={15} className="mr-1.5" /> Go to sign in
            </Button>
          </div>
        )}

        {state === "valid" && (
          <form onSubmit={accept} data-testid="invite-form">
            <div className="mb-2 inline-flex items-center gap-1.5 rounded-full border border-blue-500/30 bg-blue-500/10 px-2.5 py-0.5 text-xs font-medium text-blue-400">
              <ShieldCheck size={13} weight="fill" /> Approver invitation
            </div>
            <h1 className="font-heading text-2xl font-bold tracking-tight text-[#0F172A]">Accept your invitation</h1>
            <p className="mt-1 text-sm text-[#9CA3AF]">
              You&#39;re joining as an <span className="text-[#0F172A]">Approver</span> for{" "}
              <span className="text-[#0F172A]">{info?.email}</span>. Set your name and password to continue.
            </p>

            {error && (
              <div data-testid="invite-error" className="mt-5 rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-400">{error}</div>
            )}

            <div className="mt-6 space-y-4">
              <div>
                <Label htmlFor="iname" className="text-xs uppercase tracking-wider text-[#9CA3AF]">Full Name</Label>
                <Input id="iname" data-testid="invite-name" value={name} onChange={(e) => setName(e.target.value)} required className="mt-1.5 border-[#E2E5EA] bg-[#FFFFFF] text-[#0F172A] focus-visible:ring-red-500" />
              </div>
              <div>
                <Label htmlFor="ipass" className="text-xs uppercase tracking-wider text-[#9CA3AF]">Password (min 8 chars)</Label>
                <div className="relative mt-1.5">
                  <Input id="ipass" type={show ? "text" : "password"} data-testid="invite-password" value={password} onChange={(e) => setPassword(e.target.value)} required className="border-[#E2E5EA] bg-[#FFFFFF] pr-10 text-[#0F172A] focus-visible:ring-red-500" />
                  <button type="button" onClick={() => setShow((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6B7280] hover:text-[#0F172A]">
                    {show ? <EyeSlash size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
            </div>

            <Button type="submit" data-testid="accept-invite-submit" disabled={loading} className="mt-6 w-full bg-red-500 font-semibold text-white hover:bg-red-600">
              {loading ? "Setting up…" : "Accept & Join"}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
